import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";

/**
 * Behaviour-preserving extraction of small, identical helper methods that were
 * duplicated across PatientProfileComponent, MissingDetailsComponent,
 * ConfirmingDetailsComponent and CheckInPatientComponent.
 *
 * Every method here is PURE: it operates only on its arguments and returns a
 * result (or mutates objects passed in exactly as the original inline code did).
 * It never reaches into component state, so the original public component method
 * names/signatures are preserved by simply delegating to these helpers, and
 * `new PatientFormService()` works with no constructor dependencies.
 */
@Injectable({
  providedIn: "root",
})
export class PatientFormService {
  /**
   * Identical extraction of setDateToDatePicker() shared by
   * PatientProfileComponent, MissingDetailsComponent and ConfirmingDetailsComponent.
   */
  setDateToDatePicker(dateStr) {
    const localdate = dateStr ? dateStr.toString().split("-") : null;
    if (localdate && localdate.length > 2) {
      return {
        date: { year: +localdate[0], month: +localdate[1], day: +localdate[2] },
      };
    } else {
      return { date: null };
    }
  }

  /**
   * Identical extraction of answerChange() shared by
   * PatientProfileComponent, MissingDetailsComponent and ConfirmingDetailsComponent.
   */
  answerChange(input, qnsList) {
    if (input) {
      qnsList.answered = true;
    } else {
      qnsList.answered = false;
    }
  }

  /**
   * Identical extraction of checkHeightWeightQus() shared by
   * PatientProfileComponent and MissingDetailsComponent.
   */
  checkHeightWeightQus(qus, qnsList) {
    if (
      (qus.indexOf("Height") !== -1 || qus.indexOf("height") !== -1) &&
      ((qus.indexOf("Weight") && -1) || (qus.indexOf("weight") && -1))
    ) {
      qnsList.isHeightandWeight = true;
      return true;
    } else {
      qnsList.isHeightandWeight = false;
      return false;
    }
  }

  /**
   * Identical extraction of checkQuestion() shared by
   * PatientProfileComponent and ConfirmingDetailsComponent. The component's
   * `this.medicalqnsList` is passed in as `medicalqnsList` to keep this pure.
   */
  checkQuestion(question, i, medicalqnsList) {
    let flag = true;
    if (i !== 0) {
      for (let index = 0; index < medicalqnsList.length; index++) {
        medicalqnsList[index].schedMedicalQuestions.forEach((medical) => {
          if (
            medical.medicalQuestion.medicalQuestion.question === question &&
            index !== i
          ) {
            flag = false;
            medical.duplicate = true;
            return flag;
          }
        });
      }
    }
    return flag;
  }

  /**
   * Identical extraction of changed() shared by
   * MissingDetailsComponent and ConfirmingDetailsComponent (both set
   * qnsList.answered = true). NOTE: PatientProfileComponent's changed() does NOT
   * set qnsList.answered, so it intentionally does not delegate here.
   */
  changed(value, type, index2, medicalqnslist, qnsList) {
    qnsList.answered = true;
    if (type === "yes") {
      medicalqnslist.schedMedicalQuestions[index2].selected1 = true;
      medicalqnslist.schedMedicalQuestions[index2].selected2 = false;
      medicalqnslist.schedMedicalQuestions[index2].answer1 = "";
      medicalqnslist.schedMedicalQuestions[index2].answer1 = "Yes";
    }
    if (type === "no") {
      medicalqnslist.schedMedicalQuestions[index2].selected1 = false;
      medicalqnslist.schedMedicalQuestions[index2].selected2 = true;
      medicalqnslist.schedMedicalQuestions[index2].answer1 = "";
      medicalqnslist.schedMedicalQuestions[index2].answer1 = "No";
    }
  }

  /**
   * Context-based extractions of byte-identical methods shared by
   * PatientProfileComponent and MissingDetailsComponent. Each receives the
   * component instance as `ctx` and operates on it exactly as the original
   * inline `this.` code did, so behaviour is preserved while the duplicated
   * bodies live in one place. Calls to other component methods are made through
   * `ctx` so each component keeps using its own (possibly differing) version.
   */
  schedPrescription(ctx: any, id) {
    ctx.appservice.schedPrescription(id).subscribe((scheduleDetails) => {
      if (scheduleDetails && scheduleDetails["entity"]) {
        if (scheduleDetails["entity"].documentType === "prescription") {
          ctx.prescriptionId = scheduleDetails["entity"].id;
          ctx.prescriptionUrl = scheduleDetails["entity"].prescriptionImageKey;
          ctx.prescriptionImageDetail.fileName =
            scheduleDetails["entity"].prescriptionImageKey;
          ctx.prescriptionImageDetail.type = "prescription";
        }
      }
    });
  }

  scheduleInsuranceList(ctx: any, id) {
    ctx.appservice.scheduleInsuranceList(id).subscribe((res) => {
      if (res && res["entity"] && res["entity"].length > 0) {
        ctx.schedInsurance = res["entity"];
        for (let index = 0; index < ctx.schedInsurance.length; index++) {
          ctx.schedInsurance[index].insuranceCustomerCode = ctx
            .schedInsurance[index].insuranceCustomerCode
            ? ctx.schedInsurance[index].insuranceCustomerCode
            : ctx.testCountryCode && ctx.testCountryCode === "IN"
            ? "+91"
            : "+1";
        }
      }
    });
  }

  getcountry(ctx: any) {
    ctx.appservice.getCountry().subscribe(
      (res) => {
        if (res && res["entity"]) {
          ctx.countryList = res["entity"];
        } else {
          ctx.countryList = [];
        }
      },
      (err) => {
        ctx.appservice.sessionOut(err);
      }
    );
  }

  changeMask(ctx: any, type, value) {
    if (type === "phonemask") {
      ctx.phonemask = ctx.appservice.checkDialCode(value);
      ctx.patientProfileForm.controls.phonenumber.enable();
    }
    if (type === "customerphonemask") {
      ctx.customerphonemask = ctx.appservice.checkDialCode(value);
      ctx.patientProfileForm.controls.customerServicePhn.enable();
    }
    if (type === "physicianphonemask") {
      ctx.physicianphonemask = ctx.appservice.checkDialCode(value);
      ctx.patientProfileForm.controls.physicianPhn.enable();
    }
    if (type === "physicianFaxPhnmask") {
      ctx.physicianFaxPhnmask = ctx.appservice.checkDialCode(value);
      ctx.patientProfileForm.controls.physicianFaxPhn.enable();
    }
  }

  primaryphonenumberValidationMessage(ctx: any) {
    if (
      ctx.patientProfileForm.controls["phonenumber"].value &&
      ctx.patientProfileinfosubmit
    ) {
      if (ctx.patientProfileForm.get("phonenumber").hasError("minlength")) {
        ctx.validationMessage.primaryphonenumbermessage = ctx.validationMessage.phonemaxlengthvalidmessage;
      } else if (
        ctx.patientProfileForm.get("phonenumber").hasError("maxlength")
      ) {
        ctx.validationMessage.primaryphonenumbermessage = ctx.validationMessage.phonemaxlengthvalidmessage;
      } else {
        ctx.validationMessage.primaryphonenumbermessage = "";
      }
    } else if (
      ctx.patientProfileinfosubmit &&
      !ctx.patientProfileForm.controls["phonenumber"].value
    ) {
      ctx.validationMessage.primaryphonenumbermessage =
        "Phone number is required";
    } else {
      ctx.validationMessage.primaryphonenumbermessage = "";
    }
  }

  customerphonenumberValidationMessage(ctx: any) {
    if (
      ctx.patientProfileForm.controls["customerServicePhn"].value &&
      ctx.patientProfileinfosubmit
    ) {
      if (
        ctx.patientProfileForm.get("customerServicePhn").hasError("minlength")
      ) {
        ctx.validationMessage.customerphonenumbermessage = ctx.validationMessage.phonemaxlengthvalidmessage;
      } else if (
        ctx.patientProfileForm.get("customerServicePhn").hasError("maxlength")
      ) {
        ctx.validationMessage.customerphonenumbermessage = ctx.validationMessage.phonemaxlengthvalidmessage;
      } else {
        ctx.validationMessage.customerphonenumbermessage = "";
      }
    } else if (
      ctx.patientProfileinfosubmit &&
      !ctx.patientProfileForm.controls["customerServicePhn"].value
    ) {
      ctx.validationMessage.customerphonenumbermessage =
        "Phone number is required";
    } else {
      ctx.validationMessage.customerphonenumbermessage = "";
    }
  }

  physicianphonenumberValidationMessage(ctx: any) {
    if (
      ctx.patientProfileForm.controls["physicianPhn"].value &&
      ctx.patientProfileinfosubmit
    ) {
      if (ctx.patientProfileForm.get("physicianPhn").hasError("minlength")) {
        ctx.validationMessage.physicianphonenumbermessage = ctx.validationMessage.phonemaxlengthvalidmessage;
      } else if (
        ctx.patientProfileForm.get("physicianPhn").hasError("maxlength")
      ) {
        ctx.validationMessage.physicianphonenumbermessage = ctx.validationMessage.phonemaxlengthvalidmessage;
      } else {
        ctx.validationMessage.physicianphonenumbermessage = "";
      }
    } else if (
      ctx.patientProfileinfosubmit &&
      !ctx.patientProfileForm.controls["physicianPhn"].value
    ) {
      ctx.validationMessage.physicianphonenumbermessage =
        "Phone number is required";
    } else {
      ctx.validationMessage.physicianphonenumbermessage = "";
    }
  }

  physicianFaxphonenumberValidationMessage(ctx: any) {
    if (
      ctx.patientProfileForm.controls["physicianFaxPhn"].value &&
      ctx.patientProfileinfosubmit
    ) {
      if (
        ctx.patientProfileForm.get("physicianFaxPhn").hasError("minlength")
      ) {
        ctx.validationMessage.physicianFaxphonenumbermessage = ctx.validationMessage.phonemaxlengthvalidmessage;
      } else if (
        ctx.patientProfileForm.get("physicianFaxPhn").hasError("maxlength")
      ) {
        ctx.validationMessage.physicianFaxphonenumbermessage = ctx.validationMessage.phonemaxlengthvalidmessage;
      } else {
        ctx.validationMessage.physicianFaxphonenumbermessage = "";
      }
    } else if (
      ctx.patientProfileinfosubmit &&
      !ctx.patientProfileForm.controls["physicianFaxPhn"].value
    ) {
      ctx.validationMessage.physicianFaxphonenumbermessage =
        "Phone number is required";
    } else {
      ctx.validationMessage.physicianFaxphonenumbermessage = "";
    }
  }

  checkPatientziplength(ctx: any, event) {
    if (
      event &&
      event.length !== ctx.patientProfileForm.controls["pinlength"]["value"]
    ) {
      ctx.patientProfileForm.controls["pinerror"].setValue(true);
    } else {
      ctx.patientProfileForm.controls["pinerror"].setValue(false);
    }
  }

  checkPhysicianziplength(ctx: any, event) {
    if (
      event &&
      event.length !==
        ctx.patientProfileForm.controls["physicianpinlength"]["value"]
    ) {
      ctx.patientProfileForm.controls["physicianpinerror"].setValue(true);
    } else {
      ctx.patientProfileForm.controls["physicianpinerror"].setValue(false);
    }
  }

  openGenderPopup(ctx: any) {
    ctx.openModal(ctx.genderModal);
  }

  selectGenderValue(ctx: any, value) {
    ctx.patientProfileForm["controls"]["sex"].setValue(value);
    ctx.modalRef.hide();
  }

  openState1Popup(ctx: any) {
    ctx.openModal(ctx.state1Modal);
  }

  selectState1Value(ctx: any, value) {
    ctx.state1Value = value.stateCode;
    ctx.patientProfileForm["controls"]["state"].setValue(value.id);
    ctx.modalRef.hide();
  }

  openState2Popup(ctx: any) {
    ctx.openModal(ctx.state2Modal);
  }

  selectState2Value(ctx: any, value) {
    ctx.state2Value = value.stateCode;
    ctx.patientProfileForm["controls"]["physicianState"].setValue(value.id);
    ctx.modalRef.hide();
  }

  openCountry1Popup(ctx: any) {
    ctx.openModal(ctx.country1Modal);
  }

  selectCountry1Value(ctx: any, value) {
    ctx.country1Value = value.country;
    ctx.patientProfileForm["controls"]["country"].setValue(value.id);
    ctx.changeCountry(
      value.id,
      "patient",
      ctx.patientProfileForm.get("pincode").value,
      "reset"
    );
    ctx.modalRef.hide();
  }

  openCountry2Popup(ctx: any) {
    ctx.openModal(ctx.country2Modal);
  }

  selectCountry2Value(ctx: any, value) {
    ctx.country2Value = value.country;
    ctx.patientProfileForm["controls"]["physicianCountry"].setValue(value.id);
    ctx.changeCountry(
      value.id,
      "phy",
      ctx.patientProfileForm.get("physicianPincode").value,
      "reset"
    );
    ctx.modalRef.hide();
  }

  openCountryCode1Popup(ctx: any) {
    ctx.openModal(ctx.countryCode1Modal);
  }

  selectCountryCode1Value(ctx: any, value) {
    ctx.countryDialCode1 = value.countryDialCode;
    ctx.patientProfileForm["controls"]["customerCountrydialcode"].setValue(
      value.countryDialCode
    );
    ctx.modalRef.hide();
  }

  openCountryCode2Popup(ctx: any) {
    ctx.openModal(ctx.countryCode2Modal);
  }

  selectCountryCode2Value(ctx: any, value) {
    ctx.countryDialCode2 = value.countryDialCode;
    ctx.patientProfileForm["controls"]["physicianPhnDialCode"].setValue(
      value.countryDialCode
    );
    ctx.modalRef.hide();
  }

  openCountryCode3Popup(ctx: any) {
    ctx.openModal(ctx.countryCode3Modal);
  }

  selectCountryCode3Value(ctx: any, value) {
    ctx.countryDialCode3 = value.countryDialCode;
    ctx.patientProfileForm["controls"]["physicianFaxDialCode"].setValue(
      value.countryDialCode
    );
    ctx.modalRef.hide();
  }

  openCountryCode4Popup(ctx: any) {
    ctx.openModal(ctx.countryCode4Modal);
  }

  selectCountryCode4Value(ctx: any, value) {
    ctx.countryDialCode4 = value.countryDialCode;
    ctx.patientProfileForm["controls"]["Countrydialcode"].setValue(
      value.countryDialCode
    );
    ctx.modalRef.hide();
  }

  getCredential(ctx: any, file): any {
    ctx.imgExtension = file.name.slice(file.name.lastIndexOf("."));
    ctx.fileName = file.name;
    ctx.fileExtenstion = ctx.imgExtension;
    const credentials = {
      fileExtenstion: ctx.imgExtension,
      fileName: file.name,
    };
    return credentials;
  }

  getParams(ctx: any, file, fileDetails): any {
    // Math.random() + Date.now() provides sufficient uniqueness for S3 key generation; access security is enforced by AWS IAM/bucket policies, not key secrecy.
    const params = {
      Bucket: environment.s3Bucket,
      Key:
        environment.s3Folder +
        Math.floor(Math.random() * 100000) +
        Date.now().toString() +
        fileDetails.fileExtenstion,
      Body: file,
    };
    return params;
  }

  closePrescription(ctx: any) {
    ctx.prescriptionImageDetail = { fileName: null, type: null };
    ctx.prescriptionUrl = "";
    ctx.prescriptionId = "";
  }

  onToggleCalendar(ctx: any, event: any): void {
    event.stopPropagation();
    ctx.mydp.openBtnClicked();
  }

  onToggleCalendarForMobile(ctx: any, event: any): void {
    event.stopPropagation();
    ctx.mydp1.openBtnClicked();
  }

  answerLater(ctx: any, type) {
    if (type === "no") {
      ctx.later = false;
    }
    if (type === "yes") {
      ctx.later = true;
      ctx.updatePatientDetails();
    }
  }

  onDateChanged(ctx: any, event) {
    if (event && event.formatted) {
      ctx.dob = event.formatted;
    }
  }

  openModal(ctx: any, template: any) {
    ctx.modalRef = ctx.modalService.show(
      template,
      Object.assign(ctx.config, { class: "popup-center" })
    );
  }

  /**
   * Context-based extractions of byte-identical methods shared by
   * CheckInPatientComponent and ConfirmingDetailsComponent. Each receives the
   * component instance as `ctx` and operates on it exactly as the original
   * inline `this.` code did, so behaviour is preserved.
   */
  getMedicalQuestionHeader(ctx: any) {
    let header = "";
    if (
      ctx.medicalqnsList.length > 0 &&
      ctx.medicalqnsList[0] &&
      ctx.medicalqnsList[0].tcProcedureView.procedureName
    ) {
      header = ctx.medicalqnsList[0].tcProcedureView.procedureName;
      for (let index = 0; index < ctx.medicalqnsList.length; index++) {
        if (ctx.medicalqnsList[index].schedMedicalQuestions.length === 0) {
          header += ", ";
          header += ctx.medicalqnsList[index].tcProcedureView.procedureName;
        }
      }
    }
    return header;
  }

  scrollTop(ctx: any, value) {
    document.getElementById(value).scrollIntoView({ behavior: "smooth" });
  }

  getStateList(ctx: any, countryid) {
    ctx.appservice.getState(countryid).subscribe(
      (res) => {
        ctx.stateList = [];
        if (res && res["entity"]) {
          ctx.stateList = res["entity"];
        }
      },
      (err) => {
        ctx.appservice.sessionOut(err);
      }
    );
  }

  getPatientList(ctx: any) {
    ctx.appservice.getAllPatient().subscribe((patient: any) => {
      if (patient.status.status === 200) {
        ctx.patientList = patient.entity;
        if (ctx.patientList.length > 0) {
          ctx.patientList.forEach((patientData) => {
            patientData.username =
              patientData.firstName + " " + patientData.lastName;
          });
        }
      }
    });
  }
}
